// 修改自 https://github.com/ueberdosis/tiptap/blob/main/packages/suggestion/src/suggestion.ts
// 修复输入超过一个中文时，不触发 onUpdate 的问题
// 直接使用 customFindSuggestionMatch 替代默认的 findSuggestionMatch（支持全角和半角 @ 符号）

import type { Range } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { SuggestionOptions, SuggestionProps } from '@tiptap/suggestion';

import { customFindSuggestionMatch as defaultFindSuggestionMatch } from '../utils/custom-find-suggestion-match';

export const SuggestionPluginKey = new PluginKey('custom-suggestion');

/**
 * 等待元素出现（参考 https://github.com/ueberdosis/tiptap/issues/3031）
 * @param selector 选择器
 * @returns 元素
 */
function waitForElm(selector: string) {
  return new Promise<Element | null>((resolve) => {
    if (document.querySelector(selector)) {
      resolve(document.querySelector(selector));
      return;
    }
    const observer = new MutationObserver(() => {
      if (document.querySelector(selector)) {
        resolve(document.querySelector(selector));

        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  });
}

/**
 * This utility allows you to create suggestions.
 * @see https://tiptap.dev/api/utilities/suggestion
 */
export function createCustomSuggestionPlugin<I, TSelected>({
  pluginKey = SuggestionPluginKey,
  editor,
  char = '@',
  allowSpaces = false,
  allowToIncludeChar = false,
  allowedPrefixes = [' '],
  startOfLine = false,
  decorationTag = 'span',
  decorationClass = 'suggestion',
  command = () => null,
  items = () => [],
  render = () => ({}),
  allow = () => true,
  findSuggestionMatch = defaultFindSuggestionMatch,
}: SuggestionOptions<I, TSelected>) {
  let props: SuggestionProps<I, TSelected> | undefined;
  const renderer = render?.();

  const plugin: Plugin = new Plugin({
    key: pluginKey,

    props: {
      // Setup decorator on the currently active suggestion.
      decorations(state) {
        const { active, range, decorationId } = plugin.getState(state);

        if (!active) {
          return null;
        }

        return DecorationSet.create(state.doc, [
          Decoration.inline(range.from, range.to, {
            class: decorationClass,
            'data-decoration-id': decorationId,
            nodeName: decorationTag,
          }),
        ]);
      },
      // Call the keydown hook if suggestion is active.
      handleKeyDown(view, event) {
        const { active, range } = plugin.getState(view.state);

        if (!active) {
          return false;
        }

        return renderer?.onKeyDown?.({ event, range, view }) || false;
      },
    },

    state: {
      // Apply changes to the plugin state from a view transaction.
      apply(transaction, prev, _oldState, state) {
        const { isEditable } = editor;
        const { composing } = editor.view;
        const { selection } = transaction;
        const { empty, from } = selection;
        const next = { ...prev };

        next.composing = composing;

        // We can only be suggesting if the view is editable, and:
        //   * there is no selection, or
        //   * a composition is active (see: https://github.com/ueberdosis/tiptap/issues/1449)
        if (isEditable && (empty || editor.view.composing)) {
          // Reset active state if we just left the previous suggestion range
          if ((from < prev.range.from || from > prev.range.to) && !composing && !prev.composing) {
            next.active = false;
          }

          // Try to match against where our cursor currently is
          const match = findSuggestionMatch({
            $position: selection.$from,
            allowedPrefixes,
            allowSpaces,
            allowToIncludeChar,
            char,
            startOfLine,
          });
          const decorationId = `id_${Math.floor(Math.random() * 0xffffffff)}`;

          // If we found a match, update the current state to show it
          if (match) {
            const allowResult = allow({
              editor,
              isActive: prev.active,
              range: match.range,
              state,
            });
            if (allowResult) {
              next.active = true;
              next.decorationId = prev.decorationId ? prev.decorationId : decorationId;
              next.range = match.range;
              next.query = match.query;
              next.text = match.text;
            } else {
              next.active = false;
            }
          } else {
            next.active = false;
          }
        } else {
          next.active = false;
        }

        // Make sure to empty the range if suggestion is inactive
        if (!next.active) {
          next.decorationId = null;
          next.range = { from: 0, to: 0 };
          next.query = null;
          next.text = null;
        }

        return next;
      },
      // Initialize the plugin's internal state.
      init() {
        const state: {
          active: boolean;
          range: Range;
          query: null | string;
          text: null | string;
          composing: boolean;
          decorationId?: string | null;
        } = {
          active: false,
          composing: false,
          query: null,
          range: {
            from: 0,
            to: 0,
          },
          text: null,
        };

        return state;
      },
    },

    view() {
      return {
        destroy: () => {
          if (!props) {
            return;
          }

          renderer?.onExit?.(props);
        },
        update: async (view, prevState) => {
          const prev = this.key?.getState(prevState);
          const next = this.key?.getState(view.state);

          // See how the state changed
          const moved = prev.active && next.active && prev.range.from !== next.range.from;
          const started = !prev.active && next.active;
          const stopped = prev.active && !next.active;
          const changed = !started && !stopped && prev.query !== next.query;

          const handleStart = started || (moved && changed);
          const handleChange = changed || moved;
          const handleExit = stopped || (moved && changed);

          // Cancel when suggestion isn't active
          if (!handleStart && !handleChange && !handleExit) {
            return;
          }

          const state = handleExit && !handleStart ? prev : next;

          // 原插件代码
          // const decorationNode = view.dom.querySelector(`[data-decoration-id="${state.decorationId}"]`);

          // 修复代码
          const decorationNode = handleExit ? null : await waitForElm(`[data-decoration-id="${state.decorationId}"]`);

          props = {
            // virtual node for popper.js or tippy.js
            // this can be used for building popups without a DOM node
            clientRect: decorationNode
              ? () => {
                  // because of `items` can be asynchrounous we’ll search for the current decoration node
                  // biome-ignore lint/correctness/noUnsafeOptionalChaining: <copy from tiptap>
                  const { decorationId } = this.key?.getState(editor.state);
                  const currentDecorationNode = view.dom.querySelector(`[data-decoration-id="${decorationId}"]`);

                  return currentDecorationNode?.getBoundingClientRect() || null;
                }
              : null,
            command: (commandProps) => {
              // 修复代码配套修改：waitForElm 是异步的，await 后需要确保拿到最新的 state
              const latestState = this.key?.getState(editor.state);
              if (!latestState?.active) {
                return;
              }

              return command({
                editor,
                props: commandProps,
                range: latestState.range, // 修改代码：使用最新的 range，避免 range 不准确报错 out of range
              });
            },
            decorationNode,
            editor,
            items: [],
            query: state.query,
            range: state.range,
            text: state.text,
          };

          if (handleStart) {
            renderer?.onBeforeStart?.(props);
          }

          if (handleChange) {
            renderer?.onBeforeUpdate?.(props);
          }

          if (handleChange || handleStart) {
            props.items = await items({
              editor,
              query: state.query,
            });
          }

          if (handleExit) {
            renderer?.onExit?.(props);
          }

          if (handleChange) {
            renderer?.onUpdate?.(props);
          }

          if (handleStart) {
            renderer?.onStart?.(props);
          }
        },
      };
    },
  });

  return plugin;
}
